import type { Block, Blocks } from '@w/content-blocks'
import { clozeParagraph } from './entry-blocks'
import {
  entryParadigm,
  type CachedLexeme,
  type Lexeme,
  type RuleBlock,
  ruleQid,
} from './lexicon-model'

// D4: the class card is projected per rep, never authored. One rule = one
// stable SRS item, keyed by the rule's persisted qid; the exemplar and the cell it probes rotate
// deterministically with the rep number. No Math.random, no Date.now.
export type RuleMember = CachedLexeme | Lexeme

// Given a rule, its (derived, D1) member entries, and a rep number, return the
// Blocks for one practice rep. Deterministic: same rep + same inputs => same
// output. Only cells in `rule.predicts` are ever probed. A member without a
// WordForm for the chosen cell is skipped in favor of the next candidate,
// deterministically. Returns [] only when no member/cell pair is renderable.
export function ruleBlocks(
  rule: RuleBlock,
  members: readonly RuleMember[],
  rep: number,
): Blocks {
  const pair = rulePracticePair(rule, members, rep)
  if (!pair) return []
  const { member, cell, form } = pair
  return [clozeParagraph(ruleQid(rule), form.example, 'a0', form.example.imageSrc, [
    cell.toLocaleUpperCase(), member.headword === form.form ? '' : member.headword,
  ])]
}

/** Shared selection for rendering and the missed-cell learning action. */
export function rulePracticePair(rule: RuleBlock, members: readonly RuleMember[], rep: number) {
  const cells = rule.predicts
  const sortedMembers = members.toSorted((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  )
  const pairCount = sortedMembers.length * cells.length
  if (pairCount === 0) return null

  const start = _nonNegativeMod(rep, pairCount)
  for (let offset = 0; offset < pairCount; offset++) {
    const index = (start + offset) % pairCount
    const member = sortedMembers[index % sortedMembers.length]
    const cell = cells[Math.floor(index / sortedMembers.length) % cells.length]
    const form = entryParadigm(member).find(
      (candidate) => candidate.cell === cell,
    )
    if (!form) continue
    // Same cues as the cell card: the cell is the question, the lemma names the
    // word (withheld when it IS the answer). Without them the blank is a guess.
    return { member, cell, form }
  }
  return null
}

function _nonNegativeMod(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus
}

// A member/affix rule's class card is ONE multi-input question under the rule qid: the statement
// with every studied-language form (`l` span) blanked, plus the rotating example.
// Recalling the rule, not just applying it. Projection only; the page is unchanged.
// A rule with authored clozes (flashcards-training.requirements.md 2026-11-02) is several cards: each recall cloze, and
// its `.practice` clozes sharing one qid. `card` picks which one renders: the rule text with only that card's blanks
// (other recall blanks read as their answers), plus — for the practice card — the one item of this review. Without
// `card` (the page) every recall blank stays and one practice item renders. `rep` counts the practice qid's reviews.
export function ruleCardBlocks(
  rule: RuleBlock,
  members: readonly RuleMember[],
  rep: number,
  card?: string,
): Blocks {
  if (rule.affix) {
    const { kind, form, meaning } = rule.affix
    const answer = kind === 'suffix' ? form : meaning
    const cue = kind === 'suffix' ? `Suffix meaning: ${meaning}` : `Prefix: ${form}`
    return [clozeParagraph(ruleQid(rule), { text: answer, node: [0, answer.length], translations: {} }, 'a0', undefined, [cue])]
  }
  if (ruleHasClozes(rule)) {
    const practice = rulePracticeQid(rule.children)
    let index = -1
    const keep = card !== undefined && card !== practice ? -1 : _nonNegativeMod(rep, rulePracticeItems(rule.children).length)
    const pick = (blocks: Blocks): Blocks => blocks.flatMap((block): Blocks => {
      if (block.type === 'list') {
        const children = pick(block.children)
        return children.length > 0 ? [{ ...block, children }] : []
      }
      if (_containsCloze([block], true)) {
        index += 1
        return index === keep ? [block] : []
      }
      return [card === undefined ? block : _answered(block, card)]
    })
    return pick(rule.children)
  }
  const samples: Blocks = []
  const count = Math.min(3, rule.predicts.length)
  for (let sample = 0; sample < count; sample++) {
    const blocks = ruleBlocks(rule, members, rep + sample * members.length)
    const rename = (block: Block): Block => {
      const result = { ...block, id: `${block.id}-sample-${sample}` }
      return 'children' in result && Array.isArray(result.children)
        ? { ...result, children: (result.children as Blocks).map(child => rename(child)) } as Block
        : result
    }
    samples.push(...blocks.map(block => rename(block)))
  }
  return [...blankForms(rule.children, ruleQid(rule)), ...samples]
}

function blankForms(blocks: Blocks, qid: string): Blocks {
  return blocks.map((block): Block => {
    if (block.type === 'l') {
      return { id: block.id, type: 'cloze', qid, fractionalIndex: 'a0', children: block.children }
    }
    return 'children' in block && Array.isArray(block.children)
      ? { ...block, children: blankForms(block.children as Blocks, qid) } as Block
      : block
  })
}

function _containsCloze(blocks: Blocks, practiceOnly: boolean): boolean {
  return blocks.some(block => (block.type === 'cloze' && (!practiceOnly || block.practice === true)) ||
    ('children' in block && Array.isArray(block.children) && _containsCloze(block.children as Blocks, practiceOnly)))
}

/** A rule with authored clozes is several cards (recall clozes and its practice qid); one without is a class card. */
export function ruleHasClozes(rule: RuleBlock): boolean {
  return _containsCloze(rule.children, false)
}

/** A recall cloze of another card reads as its answer text: the card asks only its own blank. */
function _answered(block: Block, card: string): Block {
  if (!('children' in block) || !Array.isArray(block.children)) return block
  const children = (block.children as Blocks).flatMap((child): Blocks => child.type === 'cloze' && child.qid !== card
    ? child.children
    : [_answered(child, card)])
  return { ...block, children } as Block
}

/** The one qid every `.practice` cloze of a rule shares (the parser mints it), if the rule has practice items. */
export function rulePracticeQid(blocks: Blocks): string | undefined {
  for (const block of blocks) {
    if (block.type === 'cloze' && block.practice === true) return block.qid
    const found = 'children' in block && Array.isArray(block.children) ? rulePracticeQid(block.children as Blocks) : undefined
    if (found) return found
  }
  return undefined
}

/** Practice items, in order: rule children (or list items) holding a `.practice` cloze. */
export function rulePracticeItems(blocks: Blocks): Blocks {
  return blocks.flatMap((block): Blocks => block.type === 'list'
    ? rulePracticeItems(block.children)
    : _containsCloze([block], true) ? [block] : [])
}
